import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Autocomplete } from "@mui/material";
import { useDispatch } from "react-redux";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { AppDispatch } from "index";
import { fetchTermsAndConditionsOptions } from "slices/Masters/terms-and-condition/thunk";

export interface TermsAndConditionItem {
  type: string;
  options: string;
  optionId?: string | number;
}

interface TermsAndConditionsProps {
  value?: TermsAndConditionItem[] | string;
  onChange?: (items: TermsAndConditionItem[]) => void;
  formik?: any;
  name?: string;
}

// Hardcoded type options
const TERMS_TYPE_OPTIONS = [
  "After Sales Service",
  "Delivery",
  "Guarantee",
  "Price",
  "Payment",
  "Scope of Supply",
  "Validity",
  "Additional",
];

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  value,
  onChange,
  formik,
  name = "terms_and_conditions",
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedType, setSelectedType] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [optionsList, setOptionsList] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Normalize value to array
  const normalizeValue = (val: TermsAndConditionItem[] | string | undefined): TermsAndConditionItem[] => {
    if (!val) return [];
    if (typeof val === "string") {
      if (val === "") return [];
      try {
        const parsed = JSON.parse(val);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(val) ? val : [];
  };

  const [items, setItems] = useState<TermsAndConditionItem[]>(() => normalizeValue(value));

  // Sync items with value prop
  useEffect(() => {
    setItems(normalizeValue(value));
  }, [value]);

  const groupedByType = useMemo(() => {
    const map = new Map<string, TermsAndConditionItem[]>();
    for (const it of items) {
      if (!it?.type) continue;
      const arr = map.get(it.type) || [];
      arr.push(it);
      map.set(it.type, arr);
    }
    // Preserve a stable order based on the hardcoded type list, then any extras
    const ordered: { type: string; items: TermsAndConditionItem[] }[] = [];
    for (const t of TERMS_TYPE_OPTIONS) {
      const arr = map.get(t);
      if (arr && arr.length) ordered.push({ type: t, items: arr });
      map.delete(t);
    }
    map.forEach((arr, t) => {
      ordered.push({ type: t, items: arr });
    });
    return ordered;
  }, [items]);

  // Fetch options when type changes
  useEffect(() => {
    if (selectedType) {
      setLoading(true);
      dispatch(fetchTermsAndConditionsOptions(selectedType))
        .unwrap()
        .then((response: any) => {
          const options = response?.data || response || [];
          setOptionsList(options);
          setLoading(false);
        })
        .catch((error: any) => {
          console.error("Error fetching terms and conditions options:", error);
          setOptionsList([]);
          setLoading(false);
        });
    } else {
      setOptionsList([]);
      setSelectedOption(null);
    }
  }, [selectedType, dispatch]);

  const handleAdd = () => {
    if (!selectedType || !selectedOption) {
      return;
    }

    const optionText = typeof selectedOption === "string" 
      ? selectedOption 
      : selectedOption?.option_name || selectedOption?.name || selectedOption?.label || "";

    const newItem: TermsAndConditionItem = {
      type: selectedType,
      options: optionText,
      optionId: typeof selectedOption === "object"
        ? (selectedOption?.id || selectedOption?.option_id || selectedOption?.value)
        : undefined,
    };

    // One term per type: replace if type exists, otherwise add
    const existingIndex = items.findIndex((it) => it.type === selectedType);
    const updatedItems =
      existingIndex >= 0
        ? items.map((it, i) => (i === existingIndex ? newItem : it))
        : [...items, newItem];
    setItems(updatedItems);

    // Update formik if provided
    if (formik) {
      formik.setFieldValue(name, updatedItems);
    }

    // Call onChange callback if provided
    if (onChange) {
      onChange(updatedItems);
    }

    // Keep selectedType so user can add multiple options under the same type quickly
    setSelectedOption(null);
  };

  const handleRemoveType = (type: string) => {
    const updatedItems = items.filter((it) => it.type !== type);
    setItems(updatedItems);

    // Update formik if provided
    if (formik) {
      formik.setFieldValue(name, updatedItems);
    }

    // Call onChange callback if provided
    if (onChange) {
      onChange(updatedItems);
    }
  };

  // Format options for Autocomplete
  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return option?.option_name || option?.name || option?.label || "";
  };

  const isOptionEqualToValue = (option: any, value: any) => {
    if (typeof option === "string" && typeof value === "string") {
      return option === value;
    }
    const optionId = option?.id || option?.option_id || option?.value;
    const valueId = value?.id || value?.option_id || value?.value;
    return optionId === valueId;
  };

  return (
    <div>
      <h5 className="fw-bold mb-3">Terms & Conditions</h5>
      
      <Row className="mb-3">
        <Col md={5}>
          <Form.Label className="fw-semibold">Type</Form.Label>
          <Autocomplete
            options={TERMS_TYPE_OPTIONS}
            value={selectedType || null}
            onChange={(_, newValue) => {
              setSelectedType(newValue || "");
              setSelectedOption(null);
            }}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Type"
                size="small"
              />
            )}
          />
        </Col>
        
        <Col md={5}>
          <Form.Label className="fw-semibold">Options</Form.Label>
          <Autocomplete
            options={optionsList}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            value={selectedOption}
            onChange={(_, newValue) => {
              setSelectedOption(newValue);
            }}
            loading={loading}
            disabled={!selectedType || loading}
            renderInput={(params) => (
              <BootstrapTextField
                {...params}
                placeholder="Select Options"
                size="small"
                disabled={!selectedType || loading}
              />
            )}
          />
        </Col>
        
        <Col md={2} className="d-flex align-items-end">
          <Button
            variant="danger"
            size="sm"
            onClick={handleAdd}
            disabled={!selectedType || !selectedOption}
            style={{
              borderRadius: "6px",
              width: "100%",
              height: "38px",
            }}
          >
            + Add
          </Button>
        </Col>
      </Row>

      {/* Render grouped "type cards" like the screenshot */}
      {groupedByType.length > 0 && (
        <div
          className="mt-3 border rounded"
          style={{ backgroundColor: "#fff", borderColor: "#E6ECF5" }}
        >
          {groupedByType.map((group, idx) => (
            <div
              key={group.type}
              style={{
                borderBottom:
                  idx === groupedByType.length - 1 ? "none" : "1px solid #E6ECF5",
                padding: "12px 12px",
              }}
              className="d-flex justify-content-between align-items-start"
            >
              <div style={{ paddingRight: 12, flex: 1 }}>
                <div className="fw-semibold mb-2">{group.type}</div>
                <ul className="mb-0 ps-3" style={{ color: "#111827" }}>
                  {group.items.flatMap((it, i) => {
                    // Split options by \n to create separate bullet points
                    const optionsLines = it.options ? it.options.split('\n').filter(line => line.trim() !== '') : [];
                    
                    return optionsLines.map((line, lineIndex) => (
                      <li 
                        key={`${group.type}-${it.optionId ?? it.options}-${i}-${lineIndex}`}
                      >
                        {line.trim()}
                      </li>
                    ));
                  })}
                </ul>
              </div>

              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemoveType(group.type)}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                title="Remove this section"
              >
                <i className="bi bi-trash" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TermsAndConditions;

